import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dropdown, Modal } from "react-bootstrap";
import {
  getTablets,
  createTablet,
  updateTablet,
  deleteTablet,
} from "../../../services/TabletService";
import { getRooms } from "../../../services/RoomService";
import { Trans, useTranslation } from "react-i18next";
import { usePermissions, PERMISSIONS } from "../../../utils/permissions";

import i18n from "../../../i18n";
const STATUS_OPTIONS = [
  { value: "available", get label() { return i18n.t("pages.tablets.disponible"); }, badge: "success" },
  { value: "in_use", get label() { return i18n.t("pages.tablets.enCoursUtilisation"); }, badge: "info" },
  { value: "maintenance", get label() { return i18n.t("pages.tablets.enMaintenance"); }, badge: "warning" },
  { value: "lost", get label() { return i18n.t("pages.tablets.perdueHorsService"); }, badge: "danger" },
];

const statusMeta = (value) =>
  STATUS_OPTIONS.find((s) => s.value === value) || { label: value || "-", badge: "secondary" };

const emptyForm = {
  mac_address: "",
  mac_address: "",
  room_id: "",
  status: "available",
  is_active: true,
};

const Tablets = () => {
  const { t } = useTranslation();
  const can = usePermissions();
  const canCreate = can(PERMISSIONS.TABLET_CREATE);
  const canUpdate = can(PERMISSIONS.TABLET_UPDATE);
  const canDelete = can(PERMISSIONS.TABLET_DELETE);
  const [tablets, setTablets] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [roomFilter, setRoomFilter] = useState("");

  const [showFormModal, setShowFormModal] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [editingId, setEditingId] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    getRooms({ page: 1, limit: 200, is_active: true })
      .then((res) => setRooms(res.data?.data || []))
      .catch(() => setRooms([]));
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchTablets = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params = { page, limit, sort_by: "mac_address", sort_dir: "asc" };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (roomFilter) params.room_id = roomFilter;

      const res = await getTablets(params);
      const body = res.data;
      setTablets(body.data || []);
      setTotal(body.pagination?.total ?? 0);
      setTotalPages(body.pagination?.totalPages ?? 1);
    } catch (err) {
      setLoadError(err?.response?.data?.message || t("pages.tablets.impossibleChargerListeTablettes"));
      setTablets([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, statusFilter, roomFilter, t]);

  useEffect(() => {
    fetchTablets();
  }, [fetchTablets]);

  useEffect(() => {
    if (!feedback) return undefined;
    const timer = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(timer);
  }, [feedback]);

  const roomLabel = (roomId) => {
    const room = rooms.find((r) => r.id === roomId);
    return room ? `${room.code} - ${room.name}` : "-";
  };

  const resetFilters = () => {
    setSearchInput("");
    setSearch("");
    setStatusFilter("");
    setRoomFilter("");
    setPage(1);
  };

  const openAddModal = () => {
    setFormMode("add");
    setFormData(emptyForm);
    setFormErrors({});
    setSubmitError("");
    setEditingId(null);
    setShowFormModal(true);
  };

  const openEditModal = (tablet) => {
    setFormMode("edit");
    setFormData({
      mac_address: tablet.mac_address || "",
      mac_address: tablet.mac_address || "",
      room_id: tablet.room_id || "",
      status: tablet.status || "available",
      is_active: !!tablet.is_active,
    });
    setFormErrors({});
    setSubmitError("");
    setEditingId(tablet.id);
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    if (submitting) return;
    setShowFormModal(false);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.mac_address.trim()) errors.mac_address = t("pages.tablets.leNumeroInventaireEstRequis");
    if (formData.mac_address.trim() && !/^([0-9A-Fa-f]{2}[:-]){5}[0-9A-Fa-f]{2}$/.test(formData.mac_address.trim())) {
      errors.mac_address = t("pages.tablets.formatAttenduXxXxXxXx");
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    setSubmitError("");
    try {
      const payload = {
        mac_address: formData.mac_address.trim(),
        mac_address: formData.mac_address.trim() || null,
        room_id: formData.room_id || null,
        status: formData.status,
        is_active: formData.is_active,
      };

      if (formMode === "add") {
        await createTablet(payload);
        setFeedback({ type: "success", message: t("pages.tablets.tabletteCreeeAvecSucces") });
      } else {
        await updateTablet(editingId, payload);
        setFeedback({ type: "success", message: t("pages.tablets.tabletteMiseJour") });
      }

      setShowFormModal(false);
      fetchTablets();
    } catch (err) {
      setSubmitError(err?.response?.data?.message || t("common.crud.saveError"));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteTablet(deleteTarget.id);
      setFeedback({ type: "success", message: t("pages.tablets.tabletteSupprimee") });
      setDeleteTarget(null);
      if (tablets.length === 1 && page > 1) setPage((prev) => prev - 1);
      else fetchTablets();
    } catch (err) {
      setFeedback({ type: "danger", message: err?.response?.data?.message || t("pages.tablets.impossibleSupprimerTablette") });
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item"><Link to="/dashboard">{t("menu.dashboard.title")}</Link></li>
                <li className="breadcrumb-item active" aria-current="page">{t("pages.tablets.tablettes")}</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <div className="pe-1 mb-2">
              <button type="button" className="btn btn-outline-light bg-white btn-icon me-1" title={t("common.crud.refresh")} onClick={resetFilters}>
                <i className="ti ti-refresh"></i>
              </button>
            </div>
            <div className="mb-2">
              {canCreate && (
<button type="button" className="btn btn-primary d-flex align-items-center" onClick={openAddModal}>
                <i className="ti ti-square-rounded-plus me-2"></i>{t("pages.tablets.ajouterTablette")}
              </button>
)}
            </div>
          </div>
        </div>

        {feedback && (
          <div className={`alert alert-${feedback.type} alert-dismissible fade show`} role="alert">
            {feedback.message}
            <button type="button" className="btn-close" onClick={() => setFeedback(null)}></button>
          </div>
        )}

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-3">{t("pages.tablets.listeTablettes")}</h4>
            <div className="d-flex align-items-center flex-wrap">
              <div className="input-icon-start mb-3 me-2 position-relative">
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("pages.tablets.rechercherNInventaireAdresseMac")}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  style={{ minWidth: "200px" }}
                />
              </div>
              <div className="mb-3 me-2">
                <select className="form-select" value={roomFilter} onChange={(e) => { setRoomFilter(e.target.value); setPage(1); }}>
                  <option value="">{t("pages.tablets.toutesSalles")}</option>
                  {rooms.map((r) => (
                    <option key={r.id} value={r.id}>{r.code} - {r.name}</option>
                  ))}
                </select>
              </div>
              <div className="mb-3 me-2">
                <select className="form-select" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
                  <option value="">{t("common.crud.allStatuses")}</option>
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="card-body p-0 py-3">
            <div className="table-responsive">
              <table className="table datatable">
                <thead className="thead-light">
                  <tr>
                    <th>{t("pages.tablets.nInventaire")}</th>
                    <th>{t("pages.tablets.adresseMac")}</th>
                    <th>{t("pages.tablets.salleRattachement")}</th>
                    <th>{t("common.crud.statusColumn")}</th>
                    <th>{t("common.crud.actionColumn")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="5" className="text-center py-4">
                      <div className="spinner-border text-primary" role="status"><span className="visually-hidden">{t("common.crud.loading")}</span></div>
                    </td></tr>
                  ) : loadError ? (
                    <tr><td colSpan="5" className="text-center py-4 text-danger">{loadError}</td></tr>
                  ) : tablets.length > 0 ? (
                    tablets.map((tablet) => (
                      <tr key={tablet.id}>
                        <td><span className="fw-medium">{tablet.mac_address}</span></td>
                        <td>{tablet.mac_address || <span className="text-muted">-</span>}</td>
                        <td>{tablet.room_id ? roomLabel(tablet.room_id) : "-"}</td>
                        <td>
                          <span className={`badge badge-soft-${statusMeta(tablet.status).badge}`}>
                            {statusMeta(tablet.status).label}
                          </span>
                        </td>
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle as="button" className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0 i-false">
                              <i className="ti ti-dots-vertical fs-14"></i>
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end" className="p-3">
                              {canUpdate && (
<Dropdown.Item className="rounded-1" onClick={() => openEditModal(tablet)}>
                                <i className="ti ti-edit-circle me-2"></i>{t("common.crud.edit")}
                              </Dropdown.Item>
)}
                              {canDelete && (
<Dropdown.Item className="rounded-1" onClick={() => setDeleteTarget(tablet)}>
                                <i className="ti ti-trash-x me-2"></i>{t("common.crud.delete")}
                              </Dropdown.Item>
)}
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="4" className="text-center py-4">
                      <i className="ti ti-search-off fs-1 text-muted"></i>
                      <h5 className="mt-2">{t("pages.tablets.aucuneTabletteTrouvee")}</h5>
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center px-3 pt-3 border-top">
                <span className="text-sm text-gray-600">{t("pages.tablets.tablette", { total })}</span>
                <div className="d-flex gap-1">
                  <button className="btn btn-sm btn-outline-secondary" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>{t("common.ui.precedent")}</button>
                  <button className="btn btn-sm btn-outline-secondary" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>{t("common.ui.suivant")}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal show={showFormModal} onHide={closeFormModal} centered>
        <form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{formMode === "add" ? t("pages.tablets.ajouterTablette") : t("pages.tablets.modifierTablette")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {submitError && <div className="alert alert-danger">{submitError}</div>}
            <div className="mb-3">
              <label className="form-label">{t("pages.tablets.nInventaire")} <span className="text-danger">*</span></label>
              <input type="text" className={`form-control ${formErrors.mac_address ? "is-invalid" : ""}`}
                value={formData.mac_address} onChange={(e) => handleFormChange("mac_address", e.target.value)} />
              {formErrors.mac_address && <div className="invalid-feedback">{formErrors.mac_address}</div>}
            </div>
            <div className="mb-3">
              <label className="form-label">{t("pages.tablets.adresseMac")}</label>
              <input type="text" className={`form-control ${formErrors.mac_address ? "is-invalid" : ""}`}
                placeholder={t("pages.tablets.ex3c5aB41122")}
                value={formData.mac_address} onChange={(e) => handleFormChange("mac_address", e.target.value)} />
              {formErrors.mac_address && <div className="invalid-feedback">{formErrors.mac_address}</div>}
              <small className="text-muted">{t("pages.tablets.identifiantReseauTabletteUtilisePourVerifier")}</small>
            </div>
            <div className="mb-3">
              <label className="form-label">{t("pages.tablets.salleRattachement")}</label>
              <select className="form-select" value={formData.room_id} onChange={(e) => handleFormChange("room_id", e.target.value)}>
                <option value="">{t("common.fields.noneFem")}</option>
                {rooms.map((r) => (
                  <option key={r.id} value={r.id}>{r.code} - {r.name}</option>
                ))}
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label">{t("common.crud.statusColumn")}</label>
              <select className="form-select" value={formData.status} onChange={(e) => handleFormChange("status", e.target.value)}>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <div className="form-check form-switch">
              <input className="form-check-input" type="checkbox" role="switch" id="tablet-active"
                checked={formData.is_active} onChange={(e) => handleFormChange("is_active", e.target.checked)} />
              <label className="form-check-label" htmlFor="tablet-active">{t("pages.tablets.tabletteActive")}</label>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-light" onClick={closeFormModal} disabled={submitting}>{t("common.crud.cancel")}</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? t("common.crud.saving") : formMode === "add" ? t("common.crud.add") : t("common.crud.save")}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal show={deleteTarget !== null} onHide={() => (deleting ? null : setDeleteTarget(null))} centered>
        <div className="modal-body text-center">
          <span className="delete-icon"><i className="ti ti-trash-x"></i></span>
          <h4>{t("common.crud.confirmDeleteTitle")}</h4>
          <p><Trans i18nKey="pages.tablets.voulezVousVraimentSupprimerTablette" values={{ mac_address: deleteTarget?.mac_address }} components={[<strong />]} /></p>
          <div className="d-flex justify-content-center">
            <button type="button" className="btn btn-light me-3" onClick={() => setDeleteTarget(null)} disabled={deleting}>{t("common.crud.cancel")}</button>
            <button type="button" className="btn btn-danger" onClick={confirmDelete} disabled={deleting}>
              {deleting ? t("common.crud.deleting") : t("common.crud.confirmDelete")}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Tablets;
